import { Helmet } from "react-helmet-async";
import Screen from "../Layouts/Screen/Screen";
import { useEffect, useState } from "react";
import "./style.css"
import { Lock, Trash } from "react-feather";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useMessage } from "../../global-components/src";
import { useAuth } from "../../global-components/src";
import Spinner from "../../global-components/src/components/spinner/spiner";
import { BUCKET_PACOCA_URL, LIMIT_DESCRIPTION_COMMUNITY, LIMIT_NAME_COMMUNITY, PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { useTranslation } from 'react-i18next';
import { ClipboardList } from "lucide-react";
import { invalidateChatListCache } from "../../utils/chat-list-cache";

const RegisterCommunity = () => {
    const { id: communityId } = useParams();
    const [errorsInput, setErrorsInput] = useState<{ name?: string[]; description?: string[]; cover?: string[], color?: string[] }>({});
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [backgroundColor, setBackgroundColor] = useState("#5bb4ff");
    const [loading, setLoading] = useState(false);
    const [backgroundImage, setBackgroundImage] = useState("/img/img-placeholder.jpg");
    const [name, setName] = useState("");
    const isEdit = communityId && Number(communityId) > 0;
    const [description, setDescription] = useState("");
    const [postApproval, setPostApproval] = useState(false);
    const [isPrivate, setIsPrivate] = useState(false);
    const navigate = useNavigate();
    const { setMessage } = useMessage();
    const { t } = useTranslation();
    const { token, user } = useAuth();
    const textEditOrCreate = isEdit ? t("generics.edit") : t("generics.create");

    const COLORS = [
        "#5bb4ff", "#000000", "#ffc107",
        "#6c757d", "#198754", "#fdb2e1", "#A020F0",
    ];

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            const reader = new FileReader();
            reader.onload = () => { setBackgroundImage(reader.result as string); };
            reader.readAsDataURL(file);
        }
    };

    const getImagePath = (path?: string) =>
        path ? `${BUCKET_PACOCA_URL}/${path}` : "/img/img-placeholder.jpg";

    const loadDadaEdit = async () => {
        if (isEdit) {
            const response = await axios.get(`${PACOCA_API_URL}/communities/${communityId}`, {
                headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }
            });
            const data = response?.data?.community;
            if (data) {
                setName(data.name);
                setDescription(data.description);
                setBackgroundColor(data.color);
                setBackgroundImage(getImagePath(data.img));
                setPostApproval(!!data.post_approval);
                setIsPrivate(!!data.is_private);
            }
        }
    };

    useEffect(() => { loadDadaEdit(); }, []);

    const handleRemoveImage = () => { setBackgroundImage("/img/img-placeholder.jpg"); };


    const handleSubmit = async () => {
        setLoading(true);
        setErrorsInput({});
        try {
            let errors: Record<string, string[]> = {};
            if (name.length > LIMIT_NAME_COMMUNITY)
                errors.name = [t("errors.input.maxLength", { field: t("inputs.name"), max: LIMIT_NAME_COMMUNITY })];
            if (!name)
                errors.name = [t("errors.input.required", { field: t("inputs.name") })];
            if (description.length > LIMIT_DESCRIPTION_COMMUNITY)
                errors.description = [t("errors.input.maxLength", { field: t("inputs.description"), max: LIMIT_DESCRIPTION_COMMUNITY })];
            if (!description)
                errors.description = [t("errors.input.required", { field: t("inputs.description") })];
            if (!backgroundColor)
                errors.color = [t("errors.input.required", { field: t("inputs.backgroundColor") })];

            if (Object.keys(errors).length > 0)
                throw { response: { data: { errors } } };

            const formData = new FormData();
            formData.append("name", name);
            formData.append("description", description);
            formData.append("color", backgroundColor);
            formData.append("is_private", String(isPrivate ? 1 : 0));
            formData.append("post_approval", String(postApproval ? 1 : 0));
            if (backgroundImage === "/img/img-placeholder.jpg")
                formData.append("remove_image", String(true));

            if (imageFile) {
                const { data: uploadUrls } = await axios.post(
                    PACOCA_API_URL + "/generate-upload-urls",
                    { files: [{ type: "image" }] },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                await axios.put(uploadUrls[0].url, imageFile);
                formData.append("image_path", uploadUrls[0].path);
            }

            let response: any;
            if (!isEdit) {
                response = await axios.post(`${PACOCA_API_URL}/communities`, formData, {
                    headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }
                });
            } else {
                formData.append("id", communityId);
                response = await axios.post(`${PACOCA_API_URL}/communities/update/${communityId}`, formData, {
                    headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` }
                });
            }

            if (response.data.community) {
                invalidateChatListCache(user?.id);
                navigate(`/comunidades/${response.data.community.id}`);
            }

            setMessage(isEdit ? "Comunidade atualizada" : "Comunidade criada", "success");
        } catch (err: any) {
            if (err?.response?.data?.errors) {
                setErrorsInput(err.response.data.errors);
            } else {
                if (err.status === 429 && err.message?.blocked)
                    setMessage(t("generics.errors.tooManyRequests", { minutes: err.remaining }), "error");
                else if (err.status === 429)
                    setMessage(t("generics.errors.tooManyRequestsGeneric"), "error");
                else if (err.response)
                    setMessage(err.response.data.message || "Erro ao salvar comunidade!", "error");
                else if (err.request)
                    setMessage(t("generics.errors.genericServerError"), "error");
                else
                    setMessage("Ocorreu um erro inesperado!", "error");
            }
            console.error(err);
        }
        setLoading(false);
    };

    return (
        <>
            <Screen paddingTopTitle={true} title={`${textEditOrCreate} ${t("community.title")}`}>
                <Helmet>
                    <title>Paçoca - {textEditOrCreate} {t("community.title")}</title>
                    <meta name="description" content={`Paçoca - ${textEditOrCreate} ${t("community.title")} do Paçoca!`} />
                    <meta name="keywords" content={`Paçoca, rede social brasileira, ${textEditOrCreate} ${t("community.title")}}`} />
                    <meta name="author" content="Paçoca Inc." />
                    <meta property="og:title" content={`Paçoca - ${textEditOrCreate} ${t("community.title")} - Rede Social`} />
                    <meta property="og:description" content={`Paçoca - ${textEditOrCreate} ${t("community.title")}`} />
                    <meta property="og:image" content="/img/link-preview.png?v=1" />
                    <meta property="og:url" content={window.location.href} />
                    <meta property="og:type" content="website" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={`Paçoca - ${textEditOrCreate} ${t("community.title")} - Rede Social`} />
                    <meta name="twitter:description" content={`Paçoca - ${textEditOrCreate} ${t("community.title")} do Paçoca`} />
                    <meta name="twitter:image" content="/img/link-preview.png?v=1" />
                </Helmet>

                <div className="rc-form">

                    {/* ── Name + Description ───────────────────── */}
                    <div className="a">
                        <p className="rc-section-label">{t("inputs.name")}</p>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={`rc-input${errorsInput.name || name.length > LIMIT_NAME_COMMUNITY ? " rc-input-error" : ""}`}
                            type="text"
                            id="name"
                            placeholder="Nome da comunidade"
                        />
                        <div className={`rc-counter${name.length > LIMIT_NAME_COMMUNITY ? " rc-counter-over" : ""}`}>
                            {name.length}/{LIMIT_NAME_COMMUNITY}
                        </div>
                        {errorsInput.name && <p className="rc-field-error">{errorsInput.name[0]}</p>}
                    </div>

                    <div className="a">
                        <p className="rc-section-label">{t("inputs.description")}</p>
                        <input
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className={`rc-input${errorsInput.description || description.length > LIMIT_DESCRIPTION_COMMUNITY ? " rc-input-error" : ""}`}
                            type="text"
                            id="description"
                            placeholder="Descrição da comunidade"
                        />
                        <div className={`rc-counter${description.length > LIMIT_DESCRIPTION_COMMUNITY ? " rc-counter-over" : ""}`}>
                            {description.length}/{LIMIT_DESCRIPTION_COMMUNITY}
                        </div>
                        {errorsInput.description && <p className="rc-field-error">{errorsInput.description[0]}</p>}
                    </div>

                    {/* ── Cover image ──────────────────────────── */}
                    <div className="a">
                        <p className="rc-section-label">{t("inputs.banner")}</p>
                        <input
                            type="file"
                            className="d-none"
                            name="img"
                            id="img_group"
                            accept="image/*"
                            onChange={handleImageChange}
                        />
                        <label className="rc-img-upload" htmlFor="img_group">
                            <img src="/img/add-image.png" className="rc-img-upload-icon" loading="lazy" />
                            <span className="rc-img-upload-label">Escolher imagem</span>
                        </label>
                        {errorsInput.cover && <p className="rc-field-error">{errorsInput.cover[0]}</p>}
                    </div>

                    {/* ── Background color ─────────────────────── */}
                    <div className="a mt-4">
                        <p className="rc-section-label">{t("inputs.backgroundColor")}</p>
                        <div className="rc-colors">
                            {COLORS.map(color => (
                                <label
                                    key={color}
                                    className={`rc-swatch${backgroundColor === color ? " rc-swatch-active" : ""}`}
                                    style={{ backgroundColor: color }}
                                    title={color}
                                >
                                    <input
                                        type="radio"
                                        name="color"
                                        value={color}
                                        checked={backgroundColor === color}
                                        onChange={() => setBackgroundColor(color)}
                                        style={{ display: "none" }}
                                    />
                                </label>
                            ))}
                        </div>
                        {errorsInput.color && <p className="rc-field-error">{errorsInput.color[0]}</p>}
                    </div>

                    {/* ── Live preview ─────────────────────────── */}
                    <div className="rc-card rc-card-preview mt-4" style={{ backgroundColor }}>
                        <div className="rc-preview-inner">
                            <div className="rc-preview-img-wrap">
                                <img
                                    className="rc-preview-img"
                                    src={backgroundImage}
                                    onError={(e) => { e.currentTarget.src = "/img/img-placeholder.jpg"; }}
                                    alt=""
                                />
                                {backgroundImage !== "/img/img-placeholder.jpg" && (
                                    <button
                                        type="button"
                                        className="rc-preview-remove"
                                        onClick={handleRemoveImage}
                                        title="Remover imagem"
                                    >
                                        <Trash size={13} />
                                    </button>
                                )}
                            </div>
                            <div className="rc-preview-text">
                                <span className="rc-preview-name">{name || "Nome da Comunidade"}</span>
                                <span className="rc-preview-desc">{description || "Descrição da Comunidade"}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── Post approval ─────────────────────────── */}
                    <div className="a mt-4">
                        <p className="rc-section-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <ClipboardList size={16} />
                            {t("community.postApproval")}
                        </p>
                        <button
                            type="button"
                            onClick={() => setPostApproval(v => !v)}
                            style={{
                                background: postApproval ? "var(--color-primary, #3b82d4)" : "var(--background-post)",
                                border: "1px solid var(--border-color, #e5e7eb)",
                                borderRadius: "20px",
                                padding: "6px 18px",
                                cursor: "pointer",
                                fontSize: "13px",
                                color: postApproval ? "#fff" : "var(--color-text)",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            {postApproval ? t("community.postApprovalOn") : t("community.postApprovalOff")}
                        </button>
                    </div>

                    {/* ── Private community ────────────────────── */}
                    <div className="a mt-4">
                        <p className="rc-section-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <Lock size={16} />
                            {t("community.isPrivate")}
                        </p>
                        <p style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 8 }}>{t("community.isPrivateDescription")}</p>
                        <button
                            type="button"
                            onClick={() => setIsPrivate(v => !v)}
                            style={{
                                background: isPrivate ? "var(--color-primary, #3b82d4)" : "var(--background-post)",
                                border: "1px solid var(--border-color, #e5e7eb)",
                                borderRadius: "20px",
                                padding: "6px 18px",
                                cursor: "pointer",
                                fontSize: "13px",
                                color: isPrivate ? "#fff" : "var(--color-text)",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            {isPrivate ? t("community.isPrivateOn") : t("community.isPrivateOff")}
                        </button>
                    </div>

                    {/* ── Actions ──────────────────────────────── */}
                    <div className="rc-actions mt-4">
                        {isEdit && (
                            <button
                                type="button"
                                className="rc-btn rc-btn-cancel"
                                onClick={() => navigate(`/comunidades/${communityId}`)}
                            >
                                {t("generics.cancel")}
                            </button>
                        )}
                        <button
                            type="button"
                            disabled={loading}
                            className="rc-btn rc-btn-submit"
                            onClick={handleSubmit}
                        >
                            {loading ? <Spinner /> : (isEdit ? t("generics.edit") : t("generics.register"))}
                        </button>
                    </div>

                </div>
            </Screen>
        </>
    );
};

export default RegisterCommunity;
